import { Box } from '@mui/material'
import { FormikProps, useFormik } from 'formik'
import * as yup from 'yup'
import { useTranslations } from '~/src/hooks/useTranslations'
import { useEoState } from '~/src/hooks/useEoState'
import AUTHENTICATION_FLOW_STATE, { AuthenticationFlowState } from '~/src/stores/authentication-flow'
import { ConfirmationCodeFormInterface } from '~/src/types/forms/ConfirmationCodeFormInterface'
import ConfirmationCodeFormFields from '~/src/components/auth/confirmation-code/ConfirmationCodeFormFields'
import ConfirmationCodeFormActions from '~/src/components/auth/confirmation-code/ConfirmationCodeFormActions'
import useAuthenticationFlowDialogs from '~/src/hooks/auth/flow/useAuthenticationFlowDialogs'
import FormError from '~/src/components/error/FormError'
import { useEffect, useMemo } from 'react'
import { HttpErrorMessageType } from '@eo-storefronts/eo-core'

interface Props {
  fieldsColor?: 'container' | 'content',
}

const ConfirmationCodeForm = ({ fieldsColor = 'content' }: Props) => {
  const { next } = useAuthenticationFlowDialogs()
  const [ authenticationFlowState, setAuthenticationFlowState ] = useEoState<AuthenticationFlowState>(AUTHENTICATION_FLOW_STATE)
  const { t } = useTranslations()

  const formik: FormikProps<ConfirmationCodeFormInterface> = useFormik<ConfirmationCodeFormInterface>({
    initialValues: {
      confirmationCode: authenticationFlowState.confirmationCode || ''
    },
    validationSchema: yup.object({
      confirmationCode: yup
        .string()
        .min(6, t('errors.required'))
        .max(6, t('errors.required'))
        .required(t('errors.required'))
    }),
    onSubmit: () => {
      next()
    }
  })

  const showBackButton = useMemo(() => {
    const errorToTrigger: Array<HttpErrorMessageType> = [
      'CANNOT_LOGIN_EMAIL_USED_IN_MULTIPLE_ACCOUNTS',
      'CANNOT_LOGIN_PHONE_USED_IN_MULTIPLE_ACCOUNTS'
    ]

    if (!authenticationFlowState.errors) {
      return false
    }

    if (typeof authenticationFlowState.errors === 'string') {
      return errorToTrigger.includes(authenticationFlowState.errors)
    }

    return Object.values(authenticationFlowState.errors).some(errorArray =>
      errorArray.some(error => errorToTrigger.includes(error))
    )
  }, [ authenticationFlowState.errors ])

  useEffect(() => {
    setAuthenticationFlowState((state: AuthenticationFlowState) => ({
      ...state,
      confirmationCode: formik.values.confirmationCode
    }))
  }, [ formik.values ])

  return (
    <>
      {authenticationFlowState.errors && <FormError errors={authenticationFlowState.errors}/>}

      <form onSubmit={formik.handleSubmit}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <ConfirmationCodeFormFields
            form={formik}
            fieldsColor={fieldsColor} />

          <ConfirmationCodeFormActions
            loading={authenticationFlowState.isLoading}
            form={formik}
            showBackButton={showBackButton} />
        </Box>
      </form>
    </>
  )
}

export default ConfirmationCodeForm
